'use client';

import { Button } from '@ps/ui/atoms/button';
import { Dialog, DialogContent } from '@ps/ui/atoms/dialog';
import { Info } from 'lucide-react';

export type ApplySubscriptionLevelChoice =
  | 'apply-to-new-members'
  | 'apply-to-all-members';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (choice: ApplySubscriptionLevelChoice) => void;
}

/**
 * Surfaced when the creator raises a channel's minimum subscription
 * level on save. Two-choice prompt: grandfather existing members on
 * the previous tier, or apply the new requirement to everyone.
 *
 * Mirrors PopshopRN's `ChangeChannelSubscriptionLevelRemind` modal —
 * the value flows back through `updateChannel` as
 * `applySubscriptionLevelToNewMembersOnly`.
 */
export function ChangeChannelSubscriptionLevelRemind({
  open,
  onOpenChange,
  onConfirm,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="text-content-1 border-line bg-surface-1 sm:max-w-md">
        <div className="flex flex-col items-center gap-6 py-2">
          <Info className="text-brand-text h-12 w-12" aria-hidden />
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="text-lg font-semibold">
              You&rsquo;re changing subscription level of your channel
            </h2>
            <p className="text-content-2 text-sm leading-snug">
              Changing your subscription level might affect current channel
              members. Should they remain unaffected, or will they be charged
              the new rate?
            </p>
          </div>
          <div className="flex w-full flex-col gap-2">
            <Button
              type="button"
              onClick={() => onConfirm('apply-to-new-members')}
              className="bg-brand hover:bg-brand-hover-hover text-content-on-brand w-full font-semibold"
            >
              Apply to New Users Only
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onConfirm('apply-to-all-members')}
              className="border-brand text-brand-text hover:bg-brand/10 w-full bg-transparent font-semibold"
            >
              Apply to All Users
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
